"use client";

import { MessageCategoryType, MessageType } from "@/types/common";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import MessageComponent from "../shared/MessageComponent/MessageComponent";

interface MessageContextType {
  showMessage: (type: MessageCategoryType, content: string) => void;
}

const MessageContext = createContext<MessageContextType | null>(null);

export default function MessageProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<MessageType[]>([]);

  const showMessage = (type: MessageCategoryType, content: string) => {
    const id = Date.now();
    const newMessage: MessageType = { id, type, content };

    setMessages(prev => [...prev, newMessage]);

    // Auto remove messages after 3 seconds
    setTimeout(() => {
      removeMessage(id);
    }, 3000);
  };

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("flashMessage");
      if (!raw) return;
      sessionStorage.removeItem("flashMessage");
      const parsed = JSON.parse(raw) as { type?: MessageCategoryType; content?: string };
      if (parsed?.type && parsed?.content) {
        showMessage(parsed.type, parsed.content);
      }
    } catch {
      // ignore invalid flash payload
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const removeMessage = (id: number) => {
    setMessages(prev => prev?.filter(msg => msg.id !== id));
  };

  return (
    <MessageContext.Provider value={{ showMessage }}>
      {children}

      <div className="fixed top-5 left-1/2 transform -translate-x-1/2 flex flex-col gap-[15px] z-50">
        {messages?.map((msg, index) => {
          return <MessageComponent key={index} message={msg} />;
        })}
      </div>
    </MessageContext.Provider>
  );
}

export const useMessage = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessage must be used within a MessageProvider");
  }
  return context;
};
